import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Checkbox, Layout, Modal, Progress, Segmented, Select, Table, Tooltip } from 'antd'
import { Content, Header } from 'antd/es/layout/layout'
import type { ColumnsType } from 'antd/es/table'
import styles from './Health.module.scss'
import Loading from '../../../components/Loading/Loading'
import {
  CheckStatus,
  clearSelectedRun,
  getHealthRunAsync,
  getHealthRunsAsync,
  getLatestHealthAsync,
  IHealthResult,
  IHealthRun,
  IHealthRunSummary,
  OverallStatus,
  resetHealthError,
  triggerHealthRunAsync,
} from '../../../redux/admin/health'
import { useAppDispatch, useAppSelector } from '../../../redux/store'
import { StatusEnum } from '../../../redux/type'
import { notifyError, notifySuccess } from '../../../utils/Notification'

const GROUP_OPTIONS = [
  { label: 'All checks', value: 'all' },
  { label: 'Infrastructure', value: 'infrastructure' },
  { label: 'APIs', value: 'api' },
  { label: 'Integrations', value: 'integration' },
  { label: 'Business workflows', value: 'workflow' },
]

const STATUS_FILTERS = ['All', 'FAIL', 'DEGRADED', 'PASS', 'SKIPPED'] as const
type StatusFilter = (typeof STATUS_FILTERS)[number]

/** Integrations surfaced as tiles; everything else is grouped under its provider too. */
const CRITICAL_PROVIDERS = ['twilio', 'stripe', 'firebase', 'sendgrid', 'slack', 'postgres', 'redis', 'bullmq']

const OVERALL_CLASS: Record<OverallStatus, string> = {
  HEALTHY: styles.healthy,
  DEGRADED: styles.degraded,
  UNHEALTHY: styles.unhealthy,
  UNKNOWN: styles.unknown,
}

const STATUS_RANK: Record<CheckStatus, number> = { FAIL: 0, DEGRADED: 1, SKIPPED: 2, PASS: 3 }

const Pill = ({ status }: { status: CheckStatus }) => (
  <span className={`${styles.pill} ${styles[`pill${status}`]}`}>{status}</span>
)

const Chip = ({ children }: { children: ReactNode }) => <span className={styles.chip}>{children}</span>

const formatTime = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—')
const formatDuration = (ms: number | null | undefined) =>
  ms == null ? '—' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`

function worstStatus(results: IHealthResult[]): CheckStatus {
  return results.reduce<CheckStatus>(
    (worst, r) => (STATUS_RANK[r.status] < STATUS_RANK[worst] ? r.status : worst),
    'PASS',
  )
}

const Health = () => {
  const dispatch = useAppDispatch()
  const latest = useAppSelector((state) => state.admin.health.latest)
  const latestStatus = useAppSelector((state) => state.admin.health.latestStatus)
  const runs = useAppSelector((state) => state.admin.health.runs)
  const runsStatus = useAppSelector((state) => state.admin.health.runsStatus)
  const selectedRun = useAppSelector((state) => state.admin.health.selectedRun)
  const selectedRunStatus = useAppSelector((state) => state.admin.health.selectedRunStatus)
  const triggerStatus = useAppSelector((state) => state.admin.health.triggerStatus)
  const activeRunId = useAppSelector((state) => state.admin.health.activeRunId)
  const activeRun = useAppSelector((state) =>
    state.admin.health.runs.find((r) => r.runId === state.admin.health.activeRunId),
  )
  const error = useAppSelector((state) => state.admin.health.error)
  const connectionError = useAppSelector((state) => state.admin.health.connectionError)

  const [group, setGroup] = useState<string>('all')
  const [allowSideEffects, setAllowSideEffects] = useState(false)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All')
  const [providerFilter, setProviderFilter] = useState<string | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  const refresh = useCallback(() => {
    dispatch(getLatestHealthAsync())
    dispatch(getHealthRunsAsync())
  }, [dispatch])

  useEffect(() => {
    refresh()
  }, [refresh])

  // While a run is in progress, poll only the lightweight history list (no
  // result rows) every 5s, and only while the tab is visible. The full latest
  // report is fetched once, when the run finishes.
  const previousActiveRunId = useRef<string | null>(null)
  useEffect(() => {
    if (previousActiveRunId.current && !activeRunId) {
      dispatch(getLatestHealthAsync())
    }
    previousActiveRunId.current = activeRunId
    if (!activeRunId) return
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') dispatch(getHealthRunsAsync())
    }, 5000)
    return () => clearInterval(timer)
  }, [activeRunId, dispatch])

  // Tick once a second while a run is active so the elapsed time updates.
  useEffect(() => {
    if (!activeRunId) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [activeRunId])

  // While the backend is unreachable, retry quietly every 10s instead of toasting.
  useEffect(() => {
    if (!connectionError) return
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') refresh()
    }, 10000)
    return () => clearInterval(timer)
  }, [connectionError, refresh])

  useEffect(() => {
    if (error) {
      notifyError('Health check', error)
      dispatch(resetHealthError())
    }
  }, [error, dispatch])

  useEffect(() => {
    if (triggerStatus === StatusEnum.Success) {
      notifySuccess('Health check', 'Run started. Results refresh automatically.')
      dispatch(resetHealthError())
      dispatch(getHealthRunsAsync())
    }
  }, [triggerStatus, dispatch])

  const run: IHealthRun | null = selectedRun ?? latest
  const results = useMemo(() => run?.results ?? [], [run])

  const byProvider = useMemo(() => {
    const map = new Map<string, IHealthResult[]>()
    for (const r of results) {
      if (!map.has(r.provider)) map.set(r.provider, [])
      map.get(r.provider)!.push(r)
    }
    return Array.from(map.entries()).sort((a, b) => {
      const ca = CRITICAL_PROVIDERS.indexOf(a[0])
      const cb = CRITICAL_PROVIDERS.indexOf(b[0])
      if (ca !== -1 || cb !== -1) return (ca === -1 ? 99 : ca) - (cb === -1 ? 99 : cb)
      return a[0].localeCompare(b[0])
    })
  }, [results])

  const failures = useMemo(
    () =>
      results
        .filter((r) => r.status === 'FAIL' || r.status === 'DEGRADED')
        .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || a.priority.localeCompare(b.priority)),
    [results],
  )

  const filteredResults = useMemo(
    () =>
      results
        .filter((r) => statusFilter === 'All' || r.status === statusFilter)
        .filter((r) => !providerFilter || r.provider === providerFilter)
        .sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || a.testId.localeCompare(b.testId)),
    [results, statusFilter, providerFilter],
  )

  const onTrigger = () => {
    dispatch(triggerHealthRunAsync({ groups: [group], allowSideEffects }))
  }

  const openRun = (runId: string) => {
    dispatch(getHealthRunAsync(runId))
    setDetailsOpen(true)
  }

  const closeDetails = () => {
    setDetailsOpen(false)
    dispatch(clearSelectedRun())
    setProviderFilter(null)
    setStatusFilter('All')
  }

  const historyColumns: ColumnsType<IHealthRunSummary> = [
    { title: 'Started', dataIndex: 'startedAt', render: (v) => formatTime(v), width: 190 },
    {
      title: 'Overall',
      dataIndex: 'overall',
      width: 120,
      render: (v: OverallStatus) => <span className={OVERALL_CLASS[v]}>{v}</span>,
    },
    { title: 'Run', dataIndex: 'status', width: 120 },
    { title: 'Env', dataIndex: 'environment', width: 110 },
    {
      title: 'Pass / Fail / Degraded / Skipped',
      key: 'summary',
      render: (_, r) => `${r.summary.passed} / ${r.summary.failed} / ${r.summary.degraded} / ${r.summary.skipped}`,
    },
    { title: 'Critical', dataIndex: ['summary', 'criticalFailures'], width: 90 },
    { title: 'Duration', dataIndex: 'durationMs', render: (v) => formatDuration(v), width: 100 },
    { title: 'Triggered by', dataIndex: 'triggeredBy', ellipsis: true },
    { title: 'Groups', dataIndex: 'requestedGroups', render: (v: string[]) => v?.join(', ') },
  ]

  const resultColumns: ColumnsType<IHealthResult> = [
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (v: CheckStatus) => <Pill status={v} />,
    },
    { title: 'Priority', dataIndex: 'priority', width: 80 },
    {
      title: 'Check',
      key: 'check',
      render: (_, r) => (
        <div>
          <div>{r.name}</div>
          <div className={styles.mono}>
            {r.testId} · {r.kind}
            {r.sideEffect !== 'none' ? ` · ${r.sideEffect}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      width: '42%',
      render: (v) => <span style={{ wordBreak: 'break-word' }}>{v}</span>,
    },
    { title: 'Duration', dataIndex: 'durationMs', width: 90, render: (v) => formatDuration(v) },
  ]

  const isLoading = latestStatus === StatusEnum.Pending && !latest

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      <Header
        style={{
          borderBottom: '1px solid #2D2C3C',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span className={styles.headerTitle}>System Health</span>
        <div className={styles.headerControls}>
          <Select
            value={group}
            options={GROUP_OPTIONS}
            onChange={setGroup}
            style={{ width: 190 }}
            className={styles.metricSelect}
            popupClassName={styles.metricDropdown}
          />
          <Tooltip title="Also run opt-in checks that create test-mode resources or contact allow-listed test numbers/devices. Never affects real customers; blocked in production unless explicitly enabled.">
            <Checkbox
              checked={allowSideEffects}
              onChange={(e) => setAllowSideEffects(e.target.checked)}
              className={styles.checkbox}
            >
              Include opt-in live checks
            </Checkbox>
          </Tooltip>
          <Button
            type="primary"
            onClick={onTrigger}
            loading={triggerStatus === StatusEnum.Pending}
            disabled={!!activeRunId}
            // antd dims disabled text to 25% black, unreadable on the dark theme; keep the progress legible.
            style={activeRunId ? { color: '#d2d3e0', opacity: 1 } : undefined}
          >
            {activeRunId
              ? activeRun?.progress
                ? `Running… ${activeRun.progress.completed} / ${activeRun.progress.total}`
                : 'Run in progress…'
              : 'Run checks'}
          </Button>
          <Button onClick={refresh}>Refresh</Button>
          <Button onClick={() => latest && openRun(latest.runId)} disabled={!latest}>
            View detailed results
          </Button>
        </div>
      </Header>
      <Content className={styles.content}>
        {connectionError && (
          <div className={styles.banner}>
            <span className={styles.bannerDot} />
            Backend unreachable ({connectionError}). Showing the last loaded results; retrying automatically.
          </div>
        )}
        {activeRunId ? (
          <div className={styles.progressWrap}>
            <div className={`${styles.section} ${styles.progressCard}`}>
              <div className={styles.sectionTitle}>
                <span>Health check in progress</span>
                <span className={styles.mono}>
                  {activeRun?.startedAt
                    ? `elapsed ${Math.max(0, Math.round((now - new Date(activeRun.startedAt).getTime()) / 1000))}s`
                    : ''}
                </span>
              </div>
              <Progress
                percent={
                  activeRun?.progress && activeRun.progress.total > 0
                    ? Math.round((activeRun.progress.completed / activeRun.progress.total) * 100)
                    : 0
                }
                status="active"
                strokeColor="#007f7c"
                trailColor="#2c2d3c"
                showInfo={false}
              />
              <div className={styles.progressMeta}>
                {activeRun?.progress ? (
                  <>
                    <span>
                      {activeRun.progress.completed} / {activeRun.progress.total} checks completed
                    </span>
                    {activeRun.progress.currentCheckId && (
                      <span className={styles.mono}>running {activeRun.progress.currentCheckId}</span>
                    )}
                  </>
                ) : (
                  <span>Running on another replica; progress is not visible from here.</span>
                )}
              </div>
            </div>
          </div>
        ) : isLoading ? (
          <Loading />
        ) : !latest ? (
          <div className={styles.empty}>
            {connectionError
              ? 'Waiting for the backend to come back…'
              : 'No completed health check run yet. Click “Run checks” to start one.'}
          </div>
        ) : (
          <>
            <div className={styles.statRow}>
              <div className={styles.statCard}>
                <div className={styles.label}>Overall</div>
                <div className={`${styles.value} ${OVERALL_CLASS[latest.overall]}`}>{latest.overall}</div>
                <div className={styles.sub}>
                  {latest.environment} · {latest.status}
                </div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.label}>Last run</div>
                <div className={styles.value} style={{ fontSize: '16px' }}>
                  {formatTime(latest.completedAt ?? latest.startedAt)}
                </div>
                <div className={styles.sub}>took {formatDuration(latest.durationMs)}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.label}>Total</div>
                <div className={styles.value}>{latest.summary.total}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.label}>Passed</div>
                <div className={`${styles.value} ${styles.healthy}`}>{latest.summary.passed}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.label}>Failed</div>
                <div className={`${styles.value} ${styles.unhealthy}`}>{latest.summary.failed}</div>
                <div className={styles.sub}>{latest.summary.criticalFailures} critical (P0)</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.label}>Degraded</div>
                <div className={`${styles.value} ${styles.degraded}`}>{latest.summary.degraded}</div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.label}>Skipped</div>
                <div className={`${styles.value} ${styles.skipped}`}>{latest.summary.skipped}</div>
                <div className={styles.sub}>not configured / opt-in</div>
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <span>Integrations &amp; subsystems</span>
                <span className={styles.mono}>click a tile for its checks</span>
              </div>
              <div className={styles.tiles}>
                {byProvider.map(([provider, items]) => {
                  const worst = worstStatus(items)
                  const counts = items.reduce(
                    (acc: Record<CheckStatus, number>, r: IHealthResult) => ({
                      ...acc,
                      [r.status]: (acc[r.status] || 0) + 1,
                    }),
                    {} as Record<CheckStatus, number>,
                  )
                  return (
                    <div
                      key={provider}
                      className={styles.tile}
                      onClick={() => {
                        setProviderFilter(provider)
                        setStatusFilter('All')
                        openRun(latest.runId)
                      }}
                    >
                      <div className={styles.tileName}>
                        <Pill status={worst} />
                        {provider}
                      </div>
                      <div className={styles.tileCounts}>
                        {counts.PASS || 0} ok · {counts.FAIL || 0} fail · {counts.DEGRADED || 0} degraded ·{' '}
                        {counts.SKIPPED || 0} skipped
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <span>Failures &amp; remediation</span>
                <span className={styles.mono}>{failures.length} item(s)</span>
              </div>
              {failures.length === 0 ? (
                <div className={styles.empty}>Nothing failing or degraded in the latest run.</div>
              ) : (
                failures.map((r) => (
                  <div key={r.testId} className={styles.failureRow}>
                    <div className={styles.failureHead}>
                      <Pill status={r.status} />
                      <Chip>{r.priority}</Chip>
                      <span>{r.name}</span>
                      <span className={styles.mono}>{r.testId}</span>
                      {r.failureClass && <Chip>{r.failureClass}</Chip>}
                      {r.providerErrorCode && <Chip>code {r.providerErrorCode}</Chip>}
                    </div>
                    <div className={styles.failureMessage}>{r.message}</div>
                    {r.remediation && <div className={styles.remediation}>↳ {r.remediation}</div>}
                  </div>
                ))
              )}
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <span>Run history</span>
                {runsStatus === StatusEnum.Pending && <span className={styles.mono}>refreshing…</span>}
              </div>
              <Table<IHealthRunSummary>
                className={styles.darkTable}
                size="small"
                rowKey="runId"
                columns={historyColumns}
                dataSource={runs}
                scroll={{ x: 1000 }}
                pagination={{ pageSize: 10, size: 'small' }}
                onRow={(record) => ({ onClick: () => openRun(record.runId) })}
              />
            </div>
          </>
        )}
      </Content>

      <Modal
        open={detailsOpen}
        onCancel={closeDetails}
        footer={null}
        width="92vw"
        style={{ top: 24 }}
        styles={{
          header: { padding: '14px 14px 10px', borderBottom: '1px solid #2c2d3c', marginBottom: 0 },
          body: { padding: '12px 14px 14px', maxHeight: 'calc(100vh - 130px)', overflow: 'auto' },
        }}
        title={
          run ? (
            <span>
              Run {run.runId.slice(0, 8)} · <span className={OVERALL_CLASS[run.overall]}>{run.overall}</span> ·{' '}
              {formatTime(run.startedAt)} · {run.summary.passed} pass / {run.summary.failed} fail /{' '}
              {run.summary.degraded} degraded / {run.summary.skipped} skipped
            </span>
          ) : (
            'Run details'
          )
        }
      >
        {selectedRunStatus === StatusEnum.Pending && !selectedRun ? (
          <Loading />
        ) : (
          <>
            <div className={styles.headerControls} style={{ marginBottom: 12 }}>
              <Segmented
                className={styles.segmented}
                options={[...STATUS_FILTERS]}
                value={statusFilter}
                onChange={(v) => setStatusFilter(v as StatusFilter)}
              />
              <Select
                allowClear
                placeholder="Provider"
                value={providerFilter ?? undefined}
                onChange={(v) => setProviderFilter(v ?? null)}
                options={byProvider.map(([p]) => ({ label: p, value: p }))}
                style={{ width: 180 }}
              />
              <span className={styles.mono}>
                correlation {run?.correlationId} · triggered by {run?.triggeredBy} ({run?.triggerSource})
              </span>
            </div>
            <Table<IHealthResult>
              className={styles.darkTable}
              size="small"
              rowKey="testId"
              columns={resultColumns}
              dataSource={filteredResults}
              scroll={{ x: 1100 }}
              pagination={{ pageSize: 25, size: 'small' }}
              expandable={{
                expandedRowRender: (r) => (
                  <div>
                    {r.remediation && <div className={styles.remediation}>↳ {r.remediation}</div>}
                    <pre className={styles.detailsPre}>
                      {JSON.stringify(
                        {
                          category: r.category,
                          provider: r.provider,
                          failureClass: r.failureClass,
                          providerErrorCode: r.providerErrorCode,
                          startedAt: r.startedAt,
                          completedAt: r.completedAt,
                          details: r.details,
                        },
                        null,
                        2,
                      )}
                    </pre>
                  </div>
                ),
              }}
            />
          </>
        )}
      </Modal>
    </Layout>
  )
}

export default Health
